import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { LucideAngularModule, DollarSign, Users, TrendingUp, Activity } from 'lucide-angular';

export interface MetricCard {
  title: string;
  value: string;
  change: string;
  changeType: 'positive' | 'negative';
  icon: 'revenue' | 'subscriptions' | 'sales' | 'active';
  iconColor: 'brand' | 'orange' | 'blue' | 'green';
}

@Component({
  selector: 'app-metric-cards',
  standalone: true,
  imports: [CommonModule, LucideAngularModule],
  template: `
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      @for (metric of metrics; track metric.title) {
        <div class="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-md transition-shadow">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600">{{ metric.title }}</p>
              <p class="text-2xl font-bold text-gray-900 mt-2">{{ metric.value }}</p>
              <p class="text-xs mt-1 flex items-center"
                 [class]="metric.changeType === 'positive' ? 'text-success-600' : 'text-error-600'">
                <span class="mr-1">{{ metric.changeType === 'positive' ? '↗' : '↘' }}</span>
                {{ metric.change }}
              </p>
            </div>
            <div class="p-3 rounded-lg"
                 [class]="getIconBackgroundClass(metric.iconColor)">
              <lucide-icon 
                [img]="getIconType(metric.icon)"
                size="24"
                [class]="getIconColorClass(metric.iconColor)">
              </lucide-icon>
            </div>
          </div>
        </div>
      }
    </div>
  `,
  styles: ``
})
export class MetricCardsComponent {
  @Input() metrics: MetricCard[] = [];

  // Icon references
  protected readonly DollarSign = DollarSign;
  protected readonly Users = Users;
  protected readonly TrendingUp = TrendingUp;
  protected readonly Activity = Activity;

  getIconType(iconName: string): any {
    const icons = {
      'revenue': this.DollarSign,
      'subscriptions': this.Users,
      'sales': this.TrendingUp,
      'active': this.Activity
    };
    return icons[iconName as keyof typeof icons] || this.Activity;
  }

  getIconBackgroundClass(color: string): string {
    const classes = {
      'brand': 'bg-brand-50',
      'orange': 'bg-orange-50',
      'blue': 'bg-blue-light-50',
      'green': 'bg-success-50'
    };
    return classes[color as keyof typeof classes] || 'bg-gray-50';
  }

  getIconColorClass(color: string): string {
    const classes = {
      'brand': 'text-brand-500',
      'orange': 'text-orange-500',
      'blue': 'text-blue-light-500',
      'green': 'text-success-500'
    };
    return classes[color as keyof typeof classes] || 'text-gray-500';
  }
}